import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import fetchBase from "../utils/fetchBase";
import SignaturePad from "../components/SignaturePad";
import LoadingOverlay from "../components/LoadingOverlay";
import "./MyPassedShifts.css";

const STATUS_LABELS = {
    SCHEDULED: { label: "Scheduled", color: "#0071e3" },
    MISSED: { label: "Needs Veteran Initial", color: "#ff9500" },
    IN_PROGRESS: { label: "In Progress", color: "#ff9500" },
    COMPLETED: { label: "Completed", color: "#34c759" },
};

const formatPeriodDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(undefined, { month: "long", day: "numeric" });
};

const formatShiftDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

const to24HourTime = (timeStr) => {
    if (!timeStr) return "";
    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return "";
    let [, h, m, period] = match;
    h = parseInt(h, 10);
    if (period.toUpperCase() === "PM" && h < 12) h += 12;
    if (period.toUpperCase() === "AM" && h === 12) h = 0;
    return `${h.toString().padStart(2, "0")}:${m}`;
};

const computeLiveEndTime = (startTimeStr, shiftHours) => {
    if (!startTimeStr || shiftHours == null) return "";
    const [h, m] = startTimeStr.split(":").map(Number);
    if (isNaN(h) || isNaN(m)) return "";
    const date = new Date();
    const totalMinutes = h * 60 + m + Math.round(shiftHours * 60);
    date.setHours(Math.floor(totalMinutes / 60) % 24, totalMinutes % 60, 0, 0);
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const buildPeriodCalendar = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return [];
    const [sy, sm, sd] = startDateStr.split("-").map(Number);
    const [ey, em, ed] = endDateStr.split("-").map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);

    const leadingBlanks = start.getDay();
    const days = [];
    for (let i = 0; i < leadingBlanks; i++) {
        days.push(null);
    }

    const cursor = new Date(start);
    while (cursor <= end) {
        const dateStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`;
        days.push({ day: cursor.getDate(), dateStr });
        cursor.setDate(cursor.getDate() + 1);
    }
    return days;
};


    const MyPassedShifts = () => {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [periods, setPeriods] = useState([]);
    const [selectedPeriodId, setSelectedPeriodId] = useState(null);
    const [shifts, setShifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const shiftTileRefs = React.useRef({});
    const [pendingInitialShift, setPendingInitialShift] = useState(null);
    const [overlay, setOverlay] = useState(null);
    const [adjustingScheduleId, setAdjustingScheduleId] = useState(null);
    const [adjustTimeBuffer, setAdjustTimeBuffer] = useState("");
    const [pendingFinalTimesheetId, setPendingFinalTimesheetId] = useState(null);
    const [finalSignatureStage, setFinalSignatureStage] = useState(null);
    const [pendingVeteranFinalSignature, setPendingVeteranFinalSignature] = useState(null);

        const scrollToShift = (dateStr) => {
            const el = shiftTileRefs.current[dateStr];
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.style.transition = "background 0.3s";
                el.style.background = "#eef6ff";
                setTimeout(() => { el.style.background = ""; }, 1200);
            }
        };

        const isBlockedPrematureFinalDay = (shift) => {
            const sameClientShifts = shifts.filter(s => s.clientName === shift.clientName);
            const maxDate = sameClientShifts.reduce(
                (max, s) => (s.date > max ? s.date : max),
                sameClientShifts[0]?.date || shift.date
            );
            const isLatestForClient = shift.date === maxDate;
            const hasEarlierUninitialed = sameClientShifts.some(
                s => s.status === "MISSED" && s.date < shift.date
            );
            return isLatestForClient && hasEarlierUninitialed;
        };

            const handleSaveVeteranInitial = async (dataUrl) => {
                const token = localStorage.getItem("jwt_token");
                setOverlay({ message: "Saving shift record...", status: "" });
                const { scheduleId, hasFullTimeLog, timeLogId } = pendingInitialShift;

                const url = hasFullTimeLog
                    ? `${fetchBase}/time-log/${timeLogId}/veteran-initial`
                    : `${fetchBase}/time-log/schedule/${scheduleId}/veteran-initial`;

                try {
                    const res = await fetch(url, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({ veteranInitial: dataUrl })
                    });

                    if (res.ok) {
                        const data = await res.json();
                        setShifts(prev => prev.map(s =>
                            s.scheduleId === scheduleId ? { ...s, status: "COMPLETED" } : s
                        ));
                        if (data.isLastDay && data.timesheetId) {
                            setOverlay(null);
                            setPendingFinalTimesheetId(data.timesheetId);
                            setFinalSignatureStage("veteran");
                        } else {
                            setOverlay({ message: "✅Shift record completed.", status: "success" });
                        }
                    } else {
                        console.error("Failed to save veteran initial");
                        setOverlay({ message: "❌ Failed to save. Please try again.", status: "error" });
                    }
                } catch (err) {
                    console.error("Error saving veteran initial:", err);
                    setOverlay({ message: "❌ Failed to save. Please try again.", status: "error" });
                } finally {
                    setPendingInitialShift(null);
                    setTimeout(() => setOverlay(null), 2500);
                }
            };

            const handleSaveVeteranFinalSignature = (dataUrl) => {
                setPendingVeteranFinalSignature(dataUrl);
                setFinalSignatureStage("employee");
            };

            const handleSaveEmployeeSignature = async (dataUrl) => {
                const authHeader = "Bearer " + localStorage.getItem("jwt_token");
                setOverlay({ message: "Saving timesheet...", status: "" });
                try {
                    await fetch(`${fetchBase}/time-log/timesheet/${pendingFinalTimesheetId}/final-signature`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", Authorization: authHeader },
                        body: JSON.stringify({
                            veteranFinalSignature: pendingVeteranFinalSignature,
                            employeeSignature: dataUrl
                        })
                    });
                    setOverlay({ message: "✅Timesheet completed.", status: "success" });
                } catch (err) {
                    console.error("Failed to save final signatures:", err);
                    setOverlay({ message: "❌ Failed to save signatures.", status: "error" });
                } finally {
                    setFinalSignatureStage(null);
                    setPendingFinalTimesheetId(null);
                    setPendingVeteranFinalSignature(null);
                    setTimeout(() => setOverlay(null), 2500);
                }
            };

            const handleAdjustStartTime = async (scheduleId) => {
                if (!adjustTimeBuffer) {
                    setAdjustingScheduleId(null);
                    return;
                }
                const phone = localStorage.getItem("employee_phone");
                const token = localStorage.getItem("jwt_token");

                try {
                    const res = await fetch(`${fetchBase}/api/schedule/${scheduleId}/adjust-start-time`, {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({ startTime: adjustTimeBuffer, phone })
                    });

                    if (res.ok) {
                        const data = await res.json();
                        setShifts(prev => prev.map(s =>
                            s.scheduleId === scheduleId
                                ? {
                                    ...s,
                                    startTime: new Date(`1970-01-01T${data.startTime}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
                                    endTime: new Date(`1970-01-01T${data.endTime}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                                }
                                : s
                        ));
                    } else {
                        console.error("Failed to adjust start time");
                    }
                } catch (err) {
                    console.error("Error adjusting start time:", err);
                } finally {
                    setAdjustingScheduleId(null);
                }
            };

            const handleFlagUnavailable = async (scheduleId, currentlyUnavailable) => {
                const phone = localStorage.getItem("employee_phone");
                const token = localStorage.getItem("jwt_token");

                try {
                    const res = await fetch(`${fetchBase}/api/schedule/${scheduleId}/unavailable`, {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({ unavailable: !currentlyUnavailable, phone })
                    });

                    if (res.ok) {
                        const data = await res.json();
                        setShifts(prev => prev.map(s =>
                            s.scheduleId === scheduleId ? { ...s, unavailable: data.unavailable } : s
                        ));
                    } else {
                        console.error("Failed to flag schedule");
                    }
                } catch (err) {
                    console.error("Error flagging schedule:", err);
                }
            };

            useEffect(() => {
                const authHeader = "Bearer " + localStorage.getItem("jwt_token");
            fetch(`${fetchBase}/api/notifications/unread-count`, {
                headers: { Authorization: authHeader }
            })
                .then(res => res.json())
                .then(data => {
                    if (data && typeof data.count === "number") setUnreadCount(data.count);
                })
                .catch(err => console.error("Failed to fetch unread count:", err));
        }, []);

        useEffect(() => {
            const fetchPeriods = async () => {
            try {
                const phone = localStorage.getItem("employee_phone");
                const token = localStorage.getItem("jwt_token");

                if (!phone || !token) {
                    setError("Missing authentication data.");
                    setLoading(false);
                    return;
                }

                const response = await fetch(
                    `${fetchBase}/time-log/my-pay-periods?phone=${encodeURIComponent(phone)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (!response.ok) throw new Error("Failed to fetch pay periods.");

                const data = await response.json();
                setPeriods(Array.isArray(data) ? data : []);

                const active = data.find(p => p.status === "ACTIVE");
                setSelectedPeriodId(active ? active.payPeriodId : (data[0]?.payPeriodId ?? null));
            } catch (err) {
                console.error("Error fetching pay periods:", err);
                setError("Unable to load pay periods.");
                setLoading(false);
            }
        };

        fetchPeriods();
    }, []);

    useEffect(() => {
        if (!selectedPeriodId) return;

        const fetchShifts = async () => {
            setLoading(true);
            try {
                const phone = localStorage.getItem("employee_phone");
                const token = localStorage.getItem("jwt_token");

                const response = await fetch(
                    `${fetchBase}/time-log/period-shifts?phone=${encodeURIComponent(phone)}&payPeriodId=${selectedPeriodId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (!response.ok) throw new Error("Failed to fetch shifts.");

                const data = await response.json();
                setShifts(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching shifts:", err);
                setError("Unable to load shift history.");
            } finally {
                setLoading(false);
            }
        };

        fetchShifts();
    }, [selectedPeriodId]);

    const currentIndex = periods.findIndex(p => String(p.payPeriodId) === String(selectedPeriodId));

    const goToPrevious = () => {
        if (currentIndex < periods.length - 1) {
            setSelectedPeriodId(periods[currentIndex + 1].payPeriodId);
        }
    };

    const goToNext = () => {
        if (currentIndex > 0) {
            setSelectedPeriodId(periods[currentIndex - 1].payPeriodId);
        }
    };

            const selectedPeriod = periods.find(p => String(p.payPeriodId) === String(selectedPeriodId));

            console.log("DEBUG selectedPeriodId:", selectedPeriodId);
            console.log("DEBUG periods:", periods);
            console.log("DEBUG selectedPeriod:", selectedPeriod);
            console.log("DEBUG currentIndex:", currentIndex);

            const shiftsByDate = {};
            shifts.forEach(s => { shiftsByDate[s.date] = s; });

            const calendarDays = selectedPeriod
                ? buildPeriodCalendar(selectedPeriod.startDate, selectedPeriod.endDate)
                : [];

            const dayLabels = ["S", "M", "T", "W", "T", "F", "S"];

            if (error) {
        return (
            <div className="history-container">
                <p className="error-text">{error}</p>
            </div>
        );
    }

        return (
            <>
                {overlay && <LoadingOverlay message={overlay.message} status={overlay.status} />}
                <nav className="apple-global-nav">

                    <div className="left-nav">
                        <img src="/images/logoPlaceholder1.png" alt="Logo" className="apple-logo"/>
                    </div>

                    <div className="center-nav">
                        <a href="/employee/dashboard">Dashboard</a>
                    </div>

                    <div className="notification-bell-container" style={{ position: "relative", marginRight: "16px", cursor: "pointer" }} onClick={() => navigate("/employee/notifications")}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6e6e73" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                        {unreadCount > 0 && (
                            <span style={{
                                position: "absolute", top: "-6px", right: "-8px",
                                background: "#ff3b30", color: "white", fontSize: "10px",
                                fontWeight: "700", borderRadius: "10px", padding: "1px 5px",
                                minWidth: "14px", textAlign: "center"
                            }}>
                                {unreadCount}
                            </span>
                        )}
                    </div>

                    <div className="account-dropdown-container">
                        <span className="account-link">Account ▾</span>
                        <div className="account-dropdown">
                            <a href="/employee/history">History</a>
                            <a href="/employee/manual">Manual</a>
                            <a href="/logout">Log Out</a>
                        </div>
                    </div>

                    <button
                        className={`hamburger-btn ${menuOpen ? "open" : ""}`}
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        <span className="bar"></span>
                        <span className="bar"></span>
                    </button>

                </nav>

                {menuOpen && (
                    <div className="admin-mobile-menu">
                        <a href="/employee/dashboard" onClick={() => setMenuOpen(false)}>Dashboard</a>
                        <a href="/employee/history" onClick={() => setMenuOpen(false)}>History</a>
                        <a href="/employee/document-hub" onClick={() => setMenuOpen(false)}>Document Hub</a>
                        <a href="/employee/timesheets" onClick={() => setMenuOpen(false)}>Timesheets</a>
                        <a href="/employee/manual" onClick={() => setMenuOpen(false)}>Manual</a>
                        <a href="/logout" onClick={() => setMenuOpen(false)}>Log Out</a>
                    </div>
                )}

            <div className="history-container">
                <h2>My Shift History</h2>

            {selectedPeriod && (
                <div className="period-selector">
                    <button onClick={goToPrevious} disabled={currentIndex >= periods.length - 1}>
                        ‹
                    </button>
                        <span className="period-label">
                            {formatPeriodDate(selectedPeriod.startDate)} to {formatPeriodDate(selectedPeriod.endDate)}
                            {selectedPeriod.status === "ACTIVE" && " (Current)"}
                            {selectedPeriod.status === "NEXT" && " (Upcoming)"}
                        </span>
                    <button onClick={goToNext} disabled={currentIndex <= 0}>
                        ›
                    </button>
                </div>
            )}

                {!loading && calendarDays.length > 0 && (
                    <div style={{ marginBottom: "20px" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", marginBottom: "6px" }}>
                            {dayLabels.map((d, i) => (
                                <div key={i} style={{ textAlign: "center", fontSize: "11px", color: "#a0a0a0" }}>{d}</div>
                            ))}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px" }}>
                            {calendarDays.map((d, i) => {
                                if (!d) return <div key={i} />;
                                const shift = shiftsByDate[d.dateStr];
                                const statusInfo = shift ? (STATUS_LABELS[shift.status] || { color: "#e0e0e0" }) : null;
                                return (
                                    <div
                                        key={i}
                                        onClick={() => shift && scrollToShift(d.dateStr)}
                                        style={{
                                            aspectRatio: "1",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "13px",
                                            fontWeight: shift ? 600 : 400,
                                            borderRadius: "8px",
                                            cursor: shift ? "pointer" : "default",
                                            background: statusInfo ? statusInfo.color + "26" : "transparent",
                                            color: statusInfo ? statusInfo.color : "#a0a0a0",
                                        }}
                                    >
                                        {d.day}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {loading ? (
                    <p>Loading shift history...</p>
                ) : shifts.length === 0 ? (
                    <p>No shifts found for this pay period.</p>
                ) : (
                    shifts.map((shift, idx) => {
                            const statusInfo = STATUS_LABELS[shift.status] || { label: shift.status, color: "#6e6e73" };
                            const isMissed = shift.status === "MISSED";
                            const dotColor = shift.hasFullTimeLog ? "#34c759" : "#ff3b30";
                            return (
                                <div
                                    key={idx}
                                    className="history-tile"
                                    ref={(el) => { shiftTileRefs.current[shift.date] = el; }}
                                    onClick={() => {
                                        if (!isMissed) return;
                                        if (isBlockedPrematureFinalDay(shift)) {
                                            alert("Complete the earlier shift(s) for this client first — this is the last scheduled day and will finalize the timesheet.");
                                            return;
                                        }
                                        setPendingInitialShift({
                                            scheduleId: shift.scheduleId,
                                            hasFullTimeLog: shift.hasFullTimeLog,
                                            timeLogId: shift.timeLogId
                                        });
                                    }}
                                    style={isMissed ? { cursor: "pointer" } : {}}
                                >
                                <div className="history-date">{formatShiftDate(shift.date)}</div>
                                <div className="history-client">{shift.clientName || "Unknown Client"}</div>
                                    <div className="history-time" onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                        {shift.status === "SCHEDULED" && adjustingScheduleId === shift.scheduleId ? (
                                            <>
                                                <input
                                                    type="time"
                                                    autoFocus
                                                    value={adjustTimeBuffer}
                                                    onChange={(e) => setAdjustTimeBuffer(e.target.value)}
                                                    onBlur={() => handleAdjustStartTime(shift.scheduleId)}
                                                    style={{ fontSize: "13px", padding: "2px 4px" }}
                                                />
                                                <span>→ {computeLiveEndTime(adjustTimeBuffer, shift.shiftHours) || shift.endTime}</span>
                                            </>
                                        ) : (
                                            <span
                                                onClick={() => {
                                                    if (shift.status !== "SCHEDULED") return;
                                                    setAdjustTimeBuffer(to24HourTime(shift.startTime));
                                                    setAdjustingScheduleId(shift.scheduleId);
                                                }}
                                                style={shift.status === "SCHEDULED" ? { cursor: "pointer", textDecoration: "underline dotted" } : {}}
                                            >
                                                {shift.startTime} → {shift.endTime}
                                            </span>
                                        )}
                                    </div>
                                <div className="history-hours">
                                    {shift.shiftHours != null ? `${shift.shiftHours} hrs` : "--"}
                                </div>
                                    <div className="history-status" style={{ color: adjustingScheduleId === shift.scheduleId ? "#0071e3" : statusInfo.color, fontWeight: 600, fontSize: "12px", display: "flex", alignItems: "center", gap: "5px" }}>
                                        {isMissed && (
                                            <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: dotColor, display: "inline-block" }} />
                                        )}
                                        {adjustingScheduleId === shift.scheduleId ? "Editing start time" : statusInfo.label}
                                    </div>
                                <div className="unavailable-toggle-cell">
                                    {shift.status === "SCHEDULED" && (
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFlagUnavailable(shift.scheduleId, shift.unavailable);
                                            }}
                                            style={{
                                                width: "36px",
                                                height: "20px",
                                                borderRadius: "10px",
                                                background: shift.unavailable ? "#ff3b30" : "#e0e0e0",
                                                cursor: "pointer",
                                                transition: "background 0.2s",
                                                padding: "2px",
                                                boxSizing: "border-box",
                                                flexShrink: 0
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "16px",
                                                    height: "16px",
                                                    borderRadius: "50%",
                                                    background: "#fff",
                                                    boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
                                                    transform: shift.unavailable ? "translateX(16px)" : "translateX(0)",
                                                    transition: "transform 0.2s"
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                                </div>
                            );
                        })
                )}

                    <div style={{
                        marginTop: "24px",
                        padding: "16px",
                        background: "#f8f8f8",
                        borderRadius: "12px",
                        border: "0.5px solid rgba(0,0,0,0.08)"
                    }}>
                        <p style={{ fontWeight: 600, fontSize: "13px", margin: "0 0 12px", color: "#1d1d1f" }}>
                            Understanding your shift statuses
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                <span style={{ color: "#34c759", fontWeight: 600, fontSize: "12px", minWidth: "110px" }}>Completed</span>
                                <span style={{ fontSize: "12px", color: "#6e6e73" }}>You clocked in and out, and the veteran's initial was collected.</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                <span style={{ display: "flex", alignItems: "center", gap: "5px", minWidth: "110px" }}>
                                    <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#34c759", display: "inline-block" }} />
                                    <span style={{ color: "#ff9500", fontWeight: 600, fontSize: "12px" }}>Needs Initial</span>
                                </span>
                                <span style={{ fontSize: "12px", color: "#6e6e73" }}>You clocked in and out normally — we just still need the veteran's initial.</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                <span style={{ display: "flex", alignItems: "center", gap: "5px", minWidth: "110px" }}>
                                    <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#ff3b30", display: "inline-block" }} />
                                    <span style={{ color: "#ff9500", fontWeight: 600, fontSize: "12px" }}>Needs Initial</span>
                                </span>
                                <span style={{ fontSize: "12px", color: "#6e6e73" }}>No clock-in/out was recorded — this shift was added to your schedule after you already worked it. Tap it to still collect the veteran's initial for your scheduled hours.</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                <span style={{ color: "#0071e3", fontWeight: 600, fontSize: "12px", minWidth: "110px" }}>Scheduled</span>
                                <span style={{ fontSize: "12px", color: "#6e6e73" }}>An upcoming shift. You can adjust your start time or flag the day if you truly can't work it.</span>
                            </div>
                        </div>
                    </div>

                    {pendingInitialShift && (
                        <SignaturePad
                            label="Veteran Initial"
                            onSave={handleSaveVeteranInitial}
                            onClose={() => setPendingInitialShift(null)}
                            note={pendingInitialShift.hasFullTimeLog
                                ? "Collect the veteran's initial to confirm this shift."
                                : "This shift was added after it occurred. The scheduled start and end time will be recorded."}
                        />
                    )}
                    {finalSignatureStage === "veteran" && (
                        <SignaturePad label="Veteran Final Signature" onSave={handleSaveVeteranFinalSignature} saveLabel="Next" />
                    )}
                    {finalSignatureStage === "employee" && (
                        <SignaturePad label="Employee Signature" onSave={handleSaveEmployeeSignature} />
                    )}
                        </div>
                        </>
                    );
                };

        export default MyPassedShifts;